import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface PredictionRequest {
  node_indices: number[];
  model_type: 'gcn' | 'gat' | 'graphsage' | 'mlp';
}

export interface NodePrediction {
  node_id: number;
  predicted_class: number;
  class_name: string;
  confidence: number;
  probabilities: number[];
}

export interface PredictionResponse {
  predictions: NodePrediction[];
  model_used: string;
  inference_time_ms: number;
}

export interface GraphStats {
  num_nodes: number;
  num_edges: number;
  num_features: number;
  num_classes: number;
  class_distribution: Record<string, number>;
}

export interface ClassDistribution {
  labels: string[];
  values: number[];
}

export interface NodeDetails {
  node_id: number;
  true_class: string;
  feature_summary: {
    total_features: number;
    active_features: number;
    feature_density: number;
  };
  neighbors: number[];
  degree: number;
}

export const gnnApi = {
  // Predictions
  predict: (data: PredictionRequest) =>
    api.post<PredictionResponse>('/predictions/predict', data),

  predictRandom: (count: number = 5, modelType: string = 'gcn') =>
    api.get<PredictionResponse>(`/predictions/random?count=${count}&model_type=${modelType}`),

  getNodeInfo: (nodeId: number) =>
    api.get<NodeDetails>(`/predictions/node/${nodeId}`),

  getStatistics: () =>
    api.get<GraphStats>('/predictions/statistics'),

  // Visualizations
  getEmbeddings: (modelType: string = 'gcn', method: string = 'tsne') =>
    api.post('/visualizations/embeddings', { model_type: modelType, method }),

  getClassDistribution: () =>
    api.get<ClassDistribution>('/visualizations/class-distribution'),

  // Compare all models for a single node
  compareModels: async (nodeIndex: number): Promise<PredictionResponse[]> => {
    const models = ['gcn', 'gat', 'graphsage', 'mlp'] as const;
    const results = await Promise.all(
      models.map(model =>
        api.post<PredictionResponse>('/predictions/predict', {
          node_indices: [nodeIndex],
          model_type: model,
        })
      )
    );
    return results.map(r => r.data);
  },
};